import React, { useState, useEffect, useContext } from "react";
import {
  Edit,
  Camera,
  Save,
  X,
  Eye,
  EyeOff,
  ChevronRight,
  ChevronDown,
  Menu,
  User,
  Shield,
  Wallet,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { CareGiverContext } from "../context/CareGiverContext";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { toast } from "react-toastify";
import { LANGUAGES } from "../utils/constants";
import { caregiverNetAmount } from "../utils/paymentDisplay";

const Profile = () => {
  const { t } = useTranslation();
  const {
    cToken,
    profileData,
    getProfileData,
    changePassword,
    bankDetails,
    getBankDetails,
    saveBankAndActivate,
    createSubaccount,
  } = useContext(CareGiverContext);

  const { currency, backendUrl } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("profile");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    status: "",
    email: "",
    phoneNumber: "",
    dob: "",
    rating: "",
    languages: [],
    gender: "",
    experience: "",
    fees: "",
    is_booked: false,
    vetting_status: "",
    about: "",
    available: false,
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Phase 12: Bank Details State
  const [bankForm, setBankForm] = useState({
    bankCode: "",
    bankAccountNumber: "",
    bankAccountName: "",
  });
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [isActivatingSubaccount, setIsActivatingSubaccount] = useState(false);
  // Live Chapa banks
  const [chapaBanks, setChapaBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [showBankDropdown, setShowBankDropdown] = useState(false);

  // Tabs configuration
  const tabs = [
    {
      id: "profile",
      label: t("profile.title"),
      desc: t("profile.personalInfo"),
      icon: User,
    },
    {
      id: "security",
      label: t("profile.changePassword"),
      desc: t("profile.currentPassword"),
      icon: Shield,
    },
    {
      id: "payment",
      label: t("profile.paymentSetup"),
      desc: t("profile.paymentSetupDesc"),
      icon: Wallet,
    },
  ];

  // Profile data and bank details are fetched automatically by CareGiverContext on app load/cToken initialization,
  // preventing duplicate API calls when navigating to the profile page.

  // Fetch Chapa banks when payment tab is opened
  useEffect(() => {
    if (activeTab !== "payment" || chapaBanks.length > 0) return;
    setBanksLoading(true);
    axios
      .get(`${backendUrl}/api/caregiver/profile/banks`)
      .then((res) => {
        if (res.data.success) setChapaBanks(res.data.banks);
      })
      .catch(() => toast.error(t("profile.couldNotLoadBanks")))
      .finally(() => setBanksLoading(false));
  }, [activeTab, chapaBanks.length, backendUrl, t]);

  //  Update bank form when bankDetails changes
  useEffect(() => {
    if (bankDetails) {
      setBankForm({
        bankCode: bankDetails.bankCode || "",
        bankAccountNumber: bankDetails.bankAccountNumber || "",
        bankAccountName: bankDetails.bankAccountName || "",
      });
    }
  }, [bankDetails]);

  // Close bank dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".bank-dropdown-container")) {
        setShowBankDropdown(false);
      }
    };

    if (showBankDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showBankDropdown]);

  useEffect(() => {
    if (!bankDetails?.bankAccountName && profileData?.name) {
      setBankForm((prev) => ({
        ...prev,
        bankAccountName: profileData.name,
      }));
    }
  }, [bankDetails?.bankAccountName, profileData?.name]);

  // Update formData when profileData changes
  useEffect(() => {
    if (profileData) {
      // Format DOB for date input
      let formattedDob = "";
      if (profileData.dob) {
        const dobValue = profileData.dob;
        if (typeof dobValue === "string" && dobValue.includes("T")) {
          formattedDob = dobValue.split("T")[0];
        } else if (typeof dobValue === "string") {
          formattedDob = dobValue;
        } else if (dobValue instanceof Date) {
          formattedDob = dobValue.toISOString().split("T")[0];
        }
      }

      setFormData({
        name: profileData.name || "",
        category: profileData.category || "",
        status: profileData.status || "Active",
        email: profileData.email || "",
        phoneNumber: profileData.phoneNumber || "",
        dob: formattedDob,
        rating: profileData.rating || "4.0",
        languages: Array.isArray(profileData.languages)
          ? profileData.languages
          : profileData.languages?.split(",").map((lang) => lang.trim()) || [],
        gender: profileData.gender || "Female",
        experience: profileData.experience?.toString() || "3",
        fees: profileData.fees?.toString() || "6500",
        is_booked: profileData.is_booked || false,
        vetting_status: profileData.vetting_status || "Onboarded",
        about: profileData.about || "",
        available: profileData.available || false,
      });
    }
  }, [profileData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Special handling for multi-select languages
    if (name === "languages") {
      const selectedOptions = Array.from(
        e.target.selectedOptions,
        (option) => option.value,
      );
      setFormData((prev) => ({
        ...prev,
        [name]: selectedOptions,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSave = () => {
    setOpenDialog(true);
  };

  const confirmSave = async () => {
    const updateData = {
      name: formData.name,
      available: formData.available,
      about: formData.about,
      phoneNumber: formData.phoneNumber,
      dob: formData.dob,
      experience: parseInt(formData.experience) || 0,
      languages: formData.languages,
      gender: formData.gender,
    };

    try {
      const { data } = await axios.put(
        `${backendUrl}/api/caregiver/profile`,
        updateData,
        { headers: { cToken } },
      );

      if (data.success) {
        toast.success(data.message);
        setIsEditing(false);
        setOpenDialog(false);
        getProfileData(); // Refresh profile data
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("Failed to update profile");
      console.error(error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset formData based on current profileData
    if (profileData) {
      setFormData({
        name: profileData.name || "",
        category: profileData.category || "",
        status: profileData.status || "Active",
        email: profileData.email || "",
        phoneNumber: profileData.phoneNumber || "",
        dob: profileData.dob || "",
        rating: profileData.rating || "4.0",
        languages: Array.isArray(profileData.languages)
          ? profileData.languages
          : profileData.languages?.split(",").map((lang) => lang.trim()) || [],
        gender: profileData.gender || "Female",
        experience: profileData.experience?.toString() || "3",
        fees: profileData.fees?.toString() || "6500",
        is_booked: profileData.is_booked || false,
        vetting_status: profileData.vetting_status || "Onboarded",
        about: profileData.about || "",
        available: profileData.available || false,
      });
    }
  };

  // Password change handlers
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({ ...passwordForm, [name]: value });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const handlePasswordSubmit = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmPasswordChange = async (confirmed) => {
    setShowConfirmModal(false);

    if (!confirmed) return;

    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      toast.error(t("profile.allPasswordFieldsRequired"));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t("profile.passwordsDoNotMatch"));
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error(t("profile.passwordMinLength"));
      return;
    }

    const result = await changePassword(
      passwordForm.currentPassword,
      passwordForm.newPassword,
      passwordForm.confirmPassword,
    );

    if (result.success) {
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const renderPasswordStrength = () => {
    if (passwordForm.newPassword.length === 0) return null;

    const password = passwordForm.newPassword;

    // Check individual requirements
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    const hasMinLength = password.length >= 8;

    // Calculate strength percentage
    const requirements = [
      hasLowercase,
      hasUppercase,
      hasNumber,
      hasSymbol,
      hasMinLength,
    ];
    const metRequirements = requirements.filter(Boolean).length;
    const strength = (metRequirements / requirements.length) * 100;

    let color = "bg-red-500";
    let text = t("profile.weak");

    if (strength >= 60 && strength < 100) {
      color = "bg-yellow-500";
      text = t("profile.medium");
    } else if (strength >= 100) {
      color = "bg-green-500";
      text = t("profile.strong");
    }

    return (
      <div className="mt-2 space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500 dark:text-dark-muted">
            {t("profile.passwordStrengthLabel")}
          </span>
          <span
            className={`font-medium ${
              strength < 60
                ? "text-red-500"
                : strength < 100
                  ? "text-yellow-500"
                  : "text-green-500"
            }`}
          >
            {text}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full overflow-hidden bg-gray-200 dark:bg-dark-border">
          <div
            className={`h-full transition-all duration-300 ${color}`}
            style={{ width: `${strength}%` }}
          />
        </div>

        {/* Requirements checklist */}
        <div className="grid grid-cols-2 gap-1 text-xs mt-3">
          <div
            className={`flex items-center gap-1.5 ${hasMinLength ? "text-green-500" : "text-gray-400 dark:text-dark-muted"}`}
          >
            <span>{hasMinLength ? "✓" : "○"}</span>
            <span>{t("profile.requirementCharacters")}</span>
          </div>
          <div
            className={`flex items-center gap-1.5 ${hasLowercase ? "text-green-500" : "text-gray-400 dark:text-dark-muted"}`}
          >
            <span>{hasLowercase ? "✓" : "○"}</span>
            <span>{t("profile.requirementLowercase")}</span>
          </div>
          <div
            className={`flex items-center gap-1.5 ${hasUppercase ? "text-green-500" : "text-gray-400 dark:text-dark-muted"}`}
          >
            <span>{hasUppercase ? "✓" : "○"}</span>
            <span>{t("profile.requirementUppercase")}</span>
          </div>
          <div
            className={`flex items-center gap-1.5 ${hasNumber ? "text-green-500" : "text-gray-400 dark:text-dark-muted"}`}
          >
            <span>{hasNumber ? "✓" : "○"}</span>
            <span>{t("profile.requirementNumber")}</span>
          </div>
          <div
            className={`flex items-center gap-1.5 ${hasSymbol ? "text-green-500" : "text-gray-400 dark:text-dark-muted"}`}
          >
            <span>{hasSymbol ? "✓" : "○"}</span>
            <span>{t("profile.requirementSymbol")}</span>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to extract initials from caregiver name
  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  // Helper function to format experience
  const getExperienceText = (experience) => {
    return `${experience} Year${experience !== "1" ? "s" : ""}`;
  };

  // Handle tab change with mobile menu close
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setShowMobileMenu(false);
  };

  // Phase 12: Bank Details Handlers
  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankForm((prev) => {
      const nextValue =
        name === "bankAccountNumber" ? value.replace(/\D/g, "") : value;
      const next = { ...prev, [name]: nextValue };

      return next;
    });
  };

  const handleBankSave = async () => {
    if (
      !bankForm.bankCode ||
      !bankForm.bankAccountNumber ||
      !bankForm.bankAccountName
    ) {
      toast.error(t("profile.allBankFieldsRequired"));
      return;
    }
    if (!/^\d{10,20}$/.test(bankForm.bankAccountNumber)) {
      toast.error(t("profile.accountNumberDigits"));
      return;
    }
    setIsActivatingSubaccount(true);
    const result = await saveBankAndActivate({
      ...bankForm,
      bankAccountName: bankForm.bankAccountName.trim(),
    });
    setIsActivatingSubaccount(false);
    if (result.success) {
      setIsEditingBank(false);
    }
  };

  const handleBankCancel = () => {
    setIsEditingBank(false);
    // Reset to current bank details
    setBankForm({
      bankCode: bankDetails.bankCode || "",
      bankAccountNumber: bankDetails.bankAccountNumber || "",
      bankAccountName: bankDetails.bankAccountName || "",
    });
  };

  const handleActivateSubaccount = async () => {
    setIsActivatingSubaccount(true);
    try {
      const result = await createSubaccount();
      if (result.success) {
        // Refresh bank details to get updated subaccount status
        await getBankDetails();
      }
    } finally {
      setIsActivatingSubaccount(false);
    }
  };

  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber || accountNumber.length < 4) return accountNumber;
    const lastFour = accountNumber.slice(-4);
    return `****${lastFour}`;
  };

  const isBankDetailsComplete = () => {
    return !!(
      bankDetails?.bankCode &&
      bankDetails?.bankAccountNumber &&
      bankDetails?.bankAccountName
    );
  };

  const isSubaccountActive = () => {
    // getBankDetails() returns `subaccountStatus` (mapped from chapaSubaccountStatus)
    return bankDetails?.subaccountStatus === "active";
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-6xl mx-auto pb-12 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-text dark:text-dark-text">
          {t("profile.title")}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        {/* Mobile Tab Selector */}
        <div className="lg:hidden">
          <div className="relative">
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="w-full flex items-center justify-between p-4 bg-card dark:bg-dark-surface rounded-2xl border border-gray-100 dark:border-dark-border shadow-sm"
            >
              <div className="flex items-center gap-3">
                {tabs.find((t) => t.id === activeTab)?.icon && (
                  <div className="p-2 bg-primary/10 rounded-lg">
                    {React.createElement(
                      tabs.find((t) => t.id === activeTab).icon,
                      { size: 20, className: "text-primary" },
                    )}
                  </div>
                )}
                <div className="text-left">
                  <p className="font-bold text-text dark:text-dark-text">
                    {tabs.find((t) => t.id === activeTab)?.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-muted">
                    {tabs.find((t) => t.id === activeTab)?.desc}
                  </p>
                </div>
              </div>
              <Menu size={20} className="text-gray-500 dark:text-dark-muted" />
            </button>

            {/* Mobile Dropdown Menu */}
            {showMobileMenu && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-2xl border border-gray-100 dark:border-dark-border shadow-xl z-20 overflow-hidden">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full flex items-center gap-3 p-4 transition-colors ${
                        isActive
                          ? "bg-primary/5 dark:bg-primary/10 border-l-4 border-primary"
                          : "hover:bg-gray-50 dark:hover:bg-dark-surface/50 border-l-4 border-transparent"
                      }`}
                    >
                      <div>
                        <Icon
                          size={18}
                          className={
                            isActive
                              ? "text-primary"
                              : "text-gray-500 dark:text-dark-muted"
                          }
                        />
                      </div>
                      <div className="text-left">
                        <p
                          className={`font-bold ${isActive ? "text-text dark:text-dark-text" : "text-gray-600 dark:text-dark-muted"}`}
                        >
                          {tab.label}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-dark-muted">
                          {tab.desc}
                        </p>
                      </div>
                      {isActive && (
                        <ChevronRight
                          size={16}
                          className="ml-auto text-primary"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Desktop Navigation Sidebar */}
        <div className="hidden lg:block lg:col-span-3 space-y-3">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 text-left border ${
                  isActive
                    ? "bg-card dark:bg-dark-surface border-gray-200 dark:border-dark-border shadow-lg scale-[1.02]"
                    : "bg-transparent border-transparent text-gray-500 dark:text-dark-muted hover:bg-card/50 dark:hover:bg-dark-surface/50"
                }`}
              >
                <div>
                  <Icon
                    size={20}
                    className={
                      isActive
                        ? "text-primary"
                        : "text-gray-500 dark:text-dark-muted"
                    }
                  />
                </div>
                <div className="flex-1">
                  <p
                    className={`font-bold ${
                      isActive
                        ? "text-text dark:text-dark-text"
                        : "text-gray-600 dark:text-dark-muted"
                    }`}
                  >
                    {tab.label}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-dark-muted">
                    {tab.desc}
                  </p>
                </div>
                {isActive && (
                  <ChevronRight size={16} className="ml-auto text-primary" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content Area */}
        <div className="col-span-1 lg:col-span-9">
          <div className="bg-card dark:bg-dark-surface rounded-2xl md:rounded-[2rem] p-4 sm:p-6 md:p-8 lg:p-10 shadow-sm border border-gray-100 dark:border-dark-border min-h-[500px]">
            {/* Profile Tab */}
            {activeTab === "profile" && (
              <div className="space-y-6 md:space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                {/* Header/Actions */}
                <div className="flex justify-end">
                  {isEditing ? (
                    <div className="flex gap-2 md:gap-3">
                      <button
                        onClick={handleCancel}
                        className="flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-2.5 bg-card dark:bg-dark-surface border border-gray-100 dark:border-dark-border text-text dark:text-dark-text rounded-xl font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 text-sm md:text-base"
                      >
                        <X size={14} className="md:size-4" />
                        <span className="hidden sm:inline">
                          {t("profile.cancel")}
                        </span>
                        <span className="sm:hidden">{t("profile.cancel")}</span>
                      </button>
                      <button
                        onClick={handleSave}
                        className="flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors shadow-lg shadow-primary/20 text-sm md:text-base"
                      >
                        <Save size={14} className="md:size-4" />
                        <span className="hidden sm:inline">
                          {t("profile.saveChangesButton")}
                        </span>
                        <span className="sm:hidden">{t("profile.save")}</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-2.5 bg-card dark:bg-dark-surface border border-gray-100 dark:border-dark-border text-text dark:text-dark-text rounded-xl font-medium hover:border-primary hover:text-primary transition-all shadow-sm text-sm md:text-base"
                    >
                      <Edit size={14} className="md:size-4" />
                      <span className="hidden sm:inline">
                        {t("profile.editProfile")}
                      </span>
                      <span className="sm:hidden">{t("profile.edit")}</span>
                    </button>
                  )}
                </div>

                {/* Profile Header Section */}
                <div className="flex flex-col items-center pt-2 md:pt-4 pb-6 md:pb-8 px-4 md:px-8 relative">
                  <div className="relative group">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 rounded-full p-1 bg-card dark:bg-dark-surface shadow-xl flex items-center justify-center overflow-hidden">
                      {profileData?.image ? (
                        <img
                          src={profileData.image}
                          alt="Profile"
                          className="w-full h-full rounded-full object-cover border-4 border-gray-50 dark:border-gray-700"
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gradient-to-tr from-pink-600 to-rose-400 text-white flex items-center justify-center font-bold text-3xl sm:text-4xl md:text-5xl border-4 border-gray-50 dark:border-gray-700 select-none shadow-inner">
                          {getInitials(profileData?.name)}
                        </div>
                      )}
                    </div>
                    {isEditing && (
                      <button className="absolute bottom-1 right-1 md:bottom-2 md:right-2 p-1.5 md:p-2 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-colors">
                        <Camera size={14} className="md:size-[18px]" />
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="mt-4 md:mt-6 flex flex-col items-center gap-2 md:gap-3 w-full max-w-md px-4">
                      <input
                        disabled
                        value={formData.name}
                        className="text-center text-lg md:text-2xl font-bold text-gray-500 dark:text-dark-muted bg-transparent px-2 py-1 w-full cursor-not-allowed"
                      />
                      <div className="flex gap-2 md:gap-3 w-full justify-center">
                        <input
                          type="text"
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          className="text-center text-xs md:text-sm px-3 py-1 border border-gray-200 dark:border-dark-border rounded-lg bg-gray-50 dark:bg-gray-800/50 text-text dark:text-dark-text w-full sm:w-auto"
                          placeholder="Role"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <h1 className="mt-4 md:mt-6 text-xl sm:text-2xl md:text-3xl font-bold text-text dark:text-dark-text text-center">
                        {formData.name}
                      </h1>
                      <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-3 md:mt-4">
                        <span className="px-3 md:px-4 py-1 md:py-1.5 rounded-full text-xs font-semibold bg-pink-600 text-white tracking-wide">
                          {formData.category}
                        </span>
                        <span
                          className={`px-3 md:px-4 py-1 md:py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase ${
                            formData.status === "active"
                              ? "bg-emerald-600 text-white"
                              : "bg-gray-600 text-white"
                          }`}
                        >
                          {formData.status}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-12 md:gap-y-10 p-4 sm:p-6 md:p-8 lg:p-12 border-t border-gray-100 dark:border-dark-border">
                  {/* Left Column */}
                  <div className="space-y-6 md:space-y-8">
                    <div className="group">
                      <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-widest mb-2 block">
                        {t("profile.contactInformation")}
                      </label>
                      <div className="space-y-3 md:space-y-4">
                        <div>
                          <span className="text-xs md:text-sm text-gray-500 dark:text-dark-muted block mb-1">
                            {t("profile.emailAddress")}
                          </span>
                          <input
                            disabled
                            value={formData.email}
                            className="w-full text-sm md:text-base font-medium text-gray-500 dark:text-dark-muted bg-gray-50 dark:bg-gray-900/50 px-3 md:px-4 py-2 md:py-2.5 rounded-xl border border-gray-100 dark:border-dark-border cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <span className="text-xs md:text-sm text-gray-500 dark:text-dark-muted block mb-1">
                            {t("profile.phoneNumber")}
                          </span>
                          {/* Phone number is always disabled */}
                          <input
                            disabled
                            value={formData.phoneNumber}
                            className="w-full text-sm md:text-base font-medium text-gray-500 dark:text-dark-muted bg-gray-50 dark:bg-gray-900/50 px-3 md:px-4 py-2 md:py-2.5 rounded-xl border border-gray-100 dark:border-dark-border cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="group">
                      <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-widest mb-2 block">
                        {t("profile.personalDetails")}
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                        <div>
                          <span className="text-xs md:text-sm text-gray-500 dark:text-dark-muted block mb-1">
                            {t("profile.dateOfBirth")}
                          </span>
                          {isEditing ? (
                            <input
                              type="date"
                              name="dob"
                              value={formData.dob}
                              max={
                                new Date(
                                  new Date().setFullYear(
                                    new Date().getFullYear() - 18,
                                  ),
                                )
                                  .toISOString()
                                  .split("T")[0]
                              }
                              onChange={handleChange}
                              className="w-full text-sm md:text-base font-medium text-text dark:text-dark-text bg-card dark:bg-dark-surface px-3 py-2 rounded-xl border border-gray-100 dark:border-dark-border focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                            />
                          ) : (
                            <p className="text-sm md:text-base font-medium text-text dark:text-dark-text py-2">
                              {formData.dob
                                ? new Date(formData.dob).toLocaleDateString(
                                    "en-US",
                                    {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    },
                                  )
                                : t("profile.notSet")}
                            </p>
                          )}
                        </div>
                        <div>
                          <span className="text-xs md:text-sm text-gray-500 dark:text-dark-muted block mb-1">
                            {t("profile.gender")}
                          </span>
                          {isEditing ? (
                            <select
                              name="gender"
                              value={formData.gender}
                              onChange={handleChange}
                              className="w-full text-sm md:text-base font-medium text-text dark:text-dark-text bg-card dark:bg-dark-surface px-3 py-2 rounded-xl border border-gray-100 dark:border-dark-border focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                            >
                              <option value="Female">
                                {t("profile.genders.female")}
                              </option>
                              <option value="Male">
                                {t("profile.genders.male")}
                              </option>
                            </select>
                          ) : (
                            <p className="text-sm md:text-base font-medium text-text dark:text-dark-text py-2">
                              {formData.gender}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="group">
                      <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-widest mb-2 block">
                        {t("profile.skillsLanguages")}
                      </label>
                      <div>
                        <span className="text-xs md:text-sm text-gray-500 dark:text-dark-muted block mb-1">
                          {t("profile.languagesSpoken")}
                        </span>
                        {isEditing ? (
                          <div className="space-y-2">
                            {/* Multi-select dropdown */}
                            <select
                              multiple
                              name="languages"
                              value={formData.languages}
                              onChange={handleChange}
                              className="w-full text-sm md:text-base font-medium text-text dark:text-dark-text bg-card dark:bg-dark-surface px-3 py-3 rounded-xl border border-gray-100 dark:border-dark-border focus:ring-2 focus:ring-primary/20 outline-none transition-all h-32 md:h-40"
                            >
                              {LANGUAGES.map((language) => (
                                <option
                                  key={language}
                                  value={language}
                                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                                >
                                  {language}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 dark:text-dark-muted">
                              {t("profile.multiSelectHint")}
                            </p>
                            {/* Selected count display */}
                            {formData.languages.length > 0 && (
                              <p className="text-xs md:text-sm text-primary font-medium">
                                {t("profile.selectedLanguages", {
                                  count: formData.languages.length,
                                })}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {formData.languages &&
                            formData.languages.length > 0 ? (
                              formData.languages.map((lang, i) => (
                                <span
                                  key={i}
                                  className="px-2.5 md:px-3 py-1 md:py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-dark-muted rounded-lg text-xs md:text-sm font-medium border border-gray-200 dark:border-dark-border"
                                >
                                  {lang}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-500 dark:text-dark-muted italic text-sm">
                                {t("profile.noLanguagesSelected")}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6 md:space-y-8">
                    <div className="group">
                      <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-widest mb-2 block">
                        {t("profile.professionalStats")}
                      </label>
                      <div className="grid grid-cols-2 gap-3 md:gap-4">
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-3 md:p-4 rounded-2xl border border-gray-100 dark:border-dark-border">
                          <span className="text-xs md:text-sm text-gray-500 dark:text-dark-muted block mb-1">
                            {t("profile.experience")}
                          </span>
                          {isEditing ? (
                            <input
                              type="number"
                              name="experience"
                              value={formData.experience}
                              onChange={handleChange}
                              className="w-full bg-transparent border-b border-gray-300 dark:border-gray-700 focus:border-primary outline-none font-bold text-text dark:text-dark-text text-sm md:text-base"
                              min="0"
                            />
                          ) : (
                            <p className="font-bold text-text dark:text-dark-text text-sm md:text-base">
                              {getExperienceText(formData.experience)}
                            </p>
                          )}
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-3 md:p-4 rounded-2xl border border-gray-100 dark:border-dark-border">
                          <span className="text-xs md:text-sm text-gray-500 dark:text-dark-muted block mb-1">
                            {t("profile.salary")}
                          </span>
                          <p className="font-bold text-text dark:text-dark-text text-sm md:text-base">
                            {currency}{" "}
                            {caregiverNetAmount(formData.fees).toFixed(0)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="group">
                      <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-widest mb-2 block">
                        {t("profile.currentStatus")}
                      </label>
                      <div className="space-y-3 md:space-y-4">
                        <div className="flex justify-between items-center p-3 md:p-4 rounded-xl border border-gray-100 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <span className="text-xs md:text-sm font-medium text-text dark:text-dark-text">
                            {t("profile.bookingStatus")}
                          </span>
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-2 h-2 rounded-full ${
                                formData.is_booked
                                  ? "bg-red-500"
                                  : "bg-emerald-500"
                              }`}
                            ></span>
                            <span
                              className={`text-xs md:text-sm font-medium ${
                                formData.is_booked
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-emerald-600 dark:text-emerald-400"
                              }`}
                            >
                              {formData.is_booked
                                ? t("profile.booked")
                                : t("profile.availableForBooking")}
                            </span>
                          </div>
                        </div>

                        <div className="flex justify-between items-center p-3 md:p-4 rounded-xl border border-gray-100 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <span className="text-xs md:text-sm font-medium text-text dark:text-dark-text">
                            {t("profile.vettingStatus")}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            <span className="text-xs md:text-sm font-medium text-blue-600 dark:text-blue-400">
                              {formData.vetting_status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="group">
                      <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-widest mb-2 block">
                        {t("profile.about")}
                      </label>
                      {isEditing ? (
                        <textarea
                          name="about"
                          value={formData.about}
                          onChange={handleChange}
                          rows={4}
                          className="w-full text-sm md:text-base text-text dark:text-dark-text bg-card dark:bg-dark-surface p-3 md:p-4 rounded-xl border border-gray-100 dark:border-dark-border focus:ring-2 focus:ring-primary/20 outline-none resize-none leading-relaxed transition-all"
                        />
                      ) : (
                        <p className="text-xs md:text-sm text-gray-600 dark:text-dark-muted leading-relaxed bg-gray-50 dark:bg-gray-800/30 p-4 md:p-5 rounded-2xl border border-gray-100 dark:border-dark-border">
                          {formData.about || t("profile.noInformationProvided")}
                        </p>
                      )}
                    </div>

                    <div className="group">
                      <label className="text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-widest mb-2 block">
                        {t("profile.availabilityNow")}
                      </label>
                      {isEditing ? (
                        <div className="flex items-center gap-2 md:gap-3">
                          <button
                            onClick={() =>
                              setFormData((p) => ({ ...p, available: true }))
                            }
                            className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                              formData.available
                                ? "bg-primary text-white"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-dark-muted"
                            }`}
                          >
                            {t("profile.yes")}
                          </button>
                          <button
                            onClick={() =>
                              setFormData((p) => ({ ...p, available: false }))
                            }
                            className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                              !formData.available
                                ? "bg-red-500 text-white"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-dark-muted"
                            }`}
                          >
                            {t("profile.no")}
                          </button>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <span
                            className={`inline-flex items-center px-2.5 md:px-3 py-1 rounded-full text-xs font-medium text-white ${
                              formData.available
                                ? "bg-primary"
                                : "bg-red-600"
                            }`}
                          >
                            {formData.available
                              ? t("profile.yes")
                              : t("profile.no")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === "security" && (
              <div className="space-y-6 md:space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="border-b pb-4 md:pb-6 border-gray-100 dark:border-dark-border">
                  <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 text-text dark:text-dark-text">
                    {t("profile.loginSecurity")}
                  </h2>
                  <p className="text-sm md:text-base text-gray-500 dark:text-dark-muted">
                    {t("profile.loginSecurityDesc")}
                  </p>
                </div>

                {/* Password Change Form */}
                <div className="space-y-4 md:space-y-6 max-w-lg">
                  <div className="space-y-2">
                    <label className="text-xs md:text-sm font-bold text-text dark:text-dark-text">
                      {t("profile.currentPassword")}
                    </label>
                    <div className="relative">
                      <input
                        type={
                          showPasswords.currentPassword ? "text" : "password"
                        }
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 md:px-5 py-3 md:py-3.5 rounded-xl border-none outline-none focus:ring-2 focus:ring-primary/50 font-medium pr-10 bg-gray-100 dark:bg-gray-900 text-text dark:text-dark-text text-sm md:text-base"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          togglePasswordVisibility("currentPassword")
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
                      >
                        {showPasswords.currentPassword ? (
                          <EyeOff className="text-lg md:text-xl" />
                        ) : (
                          <Eye className="text-lg md:text-xl" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs md:text-sm font-bold text-text dark:text-dark-text">
                      {t("profile.newPassword")}
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.newPassword ? "text" : "password"}
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 md:px-5 py-3 md:py-3.5 rounded-xl border-none outline-none focus:ring-2 focus:ring-primary/50 font-medium pr-10 bg-gray-100 dark:bg-gray-900 text-text dark:text-dark-text text-sm md:text-base"
                        placeholder={t("profile.minCharacters")}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility("newPassword")}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
                      >
                        {showPasswords.newPassword ? (
                          <EyeOff className="text-lg md:text-xl" />
                        ) : (
                          <Eye className="text-lg md:text-xl" />
                        )}
                      </button>
                    </div>
                    {renderPasswordStrength()}
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs md:text-sm font-bold text-text dark:text-dark-text">
                      {t("profile.confirmPassword")}
                    </label>
                    <div className="relative">
                      <input
                        type={
                          showPasswords.confirmPassword ? "text" : "password"
                        }
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 md:px-5 py-3 md:py-3.5 rounded-xl border-none outline-none focus:ring-2 focus:ring-primary/50 font-medium pr-10 bg-gray-100 dark:bg-gray-900 text-text dark:text-dark-text text-sm md:text-base"
                        placeholder={t("profile.confirmNewPassword")}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          togglePasswordVisibility("confirmPassword")
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
                      >
                        {showPasswords.confirmPassword ? (
                          <EyeOff className="text-lg md:text-xl" />
                        ) : (
                          <Eye className="text-lg md:text-xl" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="pt-2 md:pt-4">
                    <button
                      onClick={handlePasswordSubmit}
                      disabled={
                        !passwordForm.currentPassword ||
                        !passwordForm.newPassword ||
                        !passwordForm.confirmPassword
                      }
                      className={`w-full sm:w-auto px-6 md:px-8 py-3 md:py-3.5 text-white rounded-xl font-bold transition-all text-sm md:text-base ${
                        !passwordForm.currentPassword ||
                        !passwordForm.newPassword ||
                        !passwordForm.confirmPassword
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-primary hover:bg-primary/90"
                      }`}
                    >
                      {t("profile.updatePassword")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Phase 12: Bank & Payment Tab */}
            {activeTab === "payment" && (
              <div className="space-y-6 md:space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="border-b pb-4 md:pb-6 border-gray-100 dark:border-dark-border">
                  <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 text-text dark:text-dark-text">
                    {t("profile.bankPaymentSetup")}
                  </h2>
                  <p className="text-sm md:text-base text-gray-500 dark:text-dark-muted">
                    {t("profile.bankPaymentSetupDesc")}
                  </p>
                </div>

                {/* Bank Details Status Card */}
                <div
                  className={`p-4 md:p-6 rounded-xl border-2 ${
                    isBankDetailsComplete() && isSubaccountActive()
                      ? "bg-emerald-600 border-emerald-700"
                      : isBankDetailsComplete()
                        ? "bg-blue-600 border-blue-700"
                        : "bg-gray-600 border-gray-700"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {isBankDetailsComplete() && isSubaccountActive() ? (
                      <CheckCircle2
                        className="text-white flex-shrink-0 mt-0.5"
                        size={24}
                      />
                    ) : (
                      <AlertCircle
                        className="text-white/80 flex-shrink-0 mt-0.5"
                        size={24}
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-white mb-1">
                        {isBankDetailsComplete() && isSubaccountActive()
                          ? t("profile.paymentAccountActive")
                          : isBankDetailsComplete()
                            ? t("profile.bankDetailsSaved")
                            : t("profile.bankDetailsNotSetUp")}
                      </h3>
                      <p className="text-sm text-white/80">
                        {isBankDetailsComplete() && isSubaccountActive()
                          ? t("profile.paymentAccountActiveDesc")
                          : isBankDetailsComplete()
                            ? t("profile.bankDetailsSavedDesc")
                            : t("profile.bankDetailsNotSetUpDesc")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bank Details Form */}
                <div className="space-y-4 md:space-y-6 max-w-2xl">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold text-text dark:text-dark-text">
                      {t("profile.bankAccountInformation")}
                    </h3>
                    {!isEditingBank && isBankDetailsComplete() && (
                      <button
                        onClick={() => setIsEditingBank(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors"
                      >
                        <Edit size={16} />
                        {t("profile.edit")}
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* Bank Selection */}
                    <div className="space-y-2">
                      <label className="text-xs md:text-sm font-bold text-text dark:text-dark-text">
                        {t("profile.bankName")}
                      </label>
                      {isEditingBank || !isBankDetailsComplete() ? (
                        <div className="relative bank-dropdown-container">
                          <button
                            type="button"
                            onClick={() =>
                              setShowBankDropdown(!showBankDropdown)
                            }
                            disabled={banksLoading}
                            className="w-full flex items-center justify-between px-4 md:px-5 py-3 md:py-3.5 rounded-xl bg-gray-50 dark:bg-gray-900 border-none text-sm md:text-base font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <span>
                              {banksLoading
                                ? t("profile.loadingBanks")
                                : bankForm.bankCode
                                  ? chapaBanks.find(
                                      (b) => String(b.id) === bankForm.bankCode,
                                    )?.name || t("profile.selectYourBank")
                                  : t("profile.selectYourBank")}
                            </span>
                            <ChevronDown
                              size={16}
                              className={`text-primary transition-transform ${
                                showBankDropdown ? "rotate-180" : ""
                              }`}
                            />
                          </button>

                          {showBankDropdown && !banksLoading && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                              <button
                                type="button"
                                onClick={() => {
                                  handleBankChange({
                                    target: { name: "bankCode", value: "" },
                                  });
                                  setShowBankDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                  bankForm.bankCode === ""
                                    ? "bg-primary text-white dark:bg-primary"
                                    : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                                }`}
                              >
                                {t("profile.selectYourBank")}
                              </button>
                              {chapaBanks.map((bank) => (
                                <button
                                  type="button"
                                  key={bank.id}
                                  onClick={() => {
                                    handleBankChange({
                                      target: {
                                        name: "bankCode",
                                        value: String(bank.id),
                                      },
                                    });
                                    setShowBankDropdown(false);
                                  }}
                                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    bankForm.bankCode === String(bank.id)
                                      ? "bg-primary text-white dark:bg-primary"
                                      : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                                  }`}
                                >
                                  {bank.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="px-4 md:px-5 py-3 md:py-3.5 rounded-xl bg-gray-100 dark:bg-gray-900 text-text dark:text-dark-text font-medium text-sm md:text-base">
                          {chapaBanks.find(
                            (b) => String(b.id) === bankForm.bankCode,
                          )?.name || bankForm.bankCode}
                        </div>
                      )}
                    </div>

                    {/* Account Number */}
                    <div className="space-y-2">
                      <label className="text-xs md:text-sm font-bold text-text dark:text-dark-text">
                        {t("profile.accountNumber")}
                      </label>
                      {isEditingBank || !isBankDetailsComplete() ? (
                        <input
                          type="text"
                          name="bankAccountNumber"
                          value={bankForm.bankAccountNumber}
                          onChange={handleBankChange}
                          placeholder={t("profile.enterAccountNumber")}
                          maxLength={20}
                          className="w-full px-4 md:px-5 py-3 md:py-3.5 rounded-xl border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary/50 font-medium bg-card dark:bg-dark-surface text-text dark:text-dark-text text-sm md:text-base"
                        />
                      ) : (
                        <div className="px-4 md:px-5 py-3 md:py-3.5 rounded-xl bg-gray-100 dark:bg-gray-900 text-text dark:text-dark-text font-medium text-sm md:text-base">
                          {maskAccountNumber(bankForm.bankAccountNumber)}
                        </div>
                      )}
                    </div>

                    {/* Account Holder Name */}
                    <div className="space-y-2">
                      <label className="text-xs md:text-sm font-bold text-text dark:text-dark-text">
                        {t("profile.accountHolderName")}
                      </label>
                      {isEditingBank || !isBankDetailsComplete() ? (
                        <input
                          type="text"
                          name="bankAccountName"
                          value={bankForm.bankAccountName}
                          placeholder={t("profile.usesProfileName")}
                          onChange={handleBankChange}
                          className="w-full px-4 md:px-5 py-3 md:py-3.5 rounded-xl border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary/50 font-medium bg-card dark:bg-dark-surface text-text dark:text-dark-text text-sm md:text-base"
                        />
                      ) : (
                        <div className="px-4 md:px-5 py-3 md:py-3.5 rounded-xl bg-gray-100 dark:bg-gray-900 text-text dark:text-dark-text font-medium text-sm md:text-base">
                          {bankForm.bankAccountName}
                        </div>
                      )}
                      {(isEditingBank || !isBankDetailsComplete()) && (
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          {t("profile.chapaValidationNote")}
                        </p>
                      )}
                    </div>

                    {/* Save & Activate — single combined action */}
                    {(isEditingBank || !isBankDetailsComplete()) && (
                      <div className="flex gap-3 pt-2">
                        {isEditingBank && (
                          <button
                            onClick={handleBankCancel}
                            disabled={isActivatingSubaccount}
                            className="px-6 py-3 border border-gray-200 dark:border-dark-border text-text dark:text-dark-text rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm md:text-base disabled:opacity-50"
                          >
                            {t("profile.cancel")}
                          </button>
                        )}
                        <button
                          onClick={handleBankSave}
                          disabled={
                            isActivatingSubaccount ||
                            !bankForm.bankCode ||
                            !bankForm.bankAccountNumber ||
                            !bankForm.bankAccountName ||
                            banksLoading
                          }
                          className={`px-6 py-3 text-white rounded-xl font-bold transition-all shadow-lg text-sm md:text-base flex items-center gap-2 ${
                            isActivatingSubaccount ||
                            !bankForm.bankCode ||
                            !bankForm.bankAccountNumber ||
                            !bankForm.bankAccountName
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-primary hover:bg-primary/90 shadow-primary/20"
                          }`}
                        >
                          {isActivatingSubaccount ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              {t("profile.savingActivating")}
                            </>
                          ) : (
                            <>
                              <Wallet size={16} />
                              {t("profile.saveActivate")}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    {/* Retry Activation — shown when bank saved but Chapa failed */}
                    {isBankDetailsComplete() &&
                      bankDetails?.subaccountStatus === "failed" &&
                      !isEditingBank && (
                        <div className="pt-2">
                          <p className="text-xs text-red-500 dark:text-red-400 mb-2">
                            {t("profile.paymentAccountActivationFailed")}
                          </p>
                          <button
                            onClick={handleActivateSubaccount}
                            disabled={isActivatingSubaccount}
                            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all shadow-lg text-sm flex items-center gap-2 disabled:opacity-60"
                          >
                            {isActivatingSubaccount ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                {t("profile.retrying")}
                              </>
                            ) : (
                              <>
                                <Wallet size={16} />
                                {t("profile.retryActivation")}
                              </>
                            )}
                          </button>
                        </div>
                      )}
                  </div>
                </div>

                {/* Success Message */}
                {isBankDetailsComplete() && isSubaccountActive() && (
                  <div className="max-w-2xl">
                    <div className="p-6 bg-emerald-600 border-2 border-emerald-700 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <CheckCircle2
                          className="text-white"
                          size={24}
                        />
                        <h3 className="font-bold text-white">
                          {t("profile.paymentAccountActiveSuccess")}
                        </h3>
                      </div>
                      <p className="text-sm text-white/80">
                        {t("profile.paymentAccountActiveSuccessDesc")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Save Confirmation Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card dark:bg-dark-surface p-5 md:p-6 rounded-xl shadow-lg max-w-sm w-full mx-4 border border-gray-100 dark:border-dark-border">
            <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-text dark:text-dark-text">
              {t("profile.confirmChanges")}
            </h2>
            <p className="mb-4 md:mb-6 text-sm md:text-base text-gray-600 dark:text-dark-muted">
              {t("profile.confirmProfileChanges")}
            </p>
            <div className="flex justify-end gap-2 md:gap-3">
              <button
                onClick={() => setOpenDialog(false)}
                className="px-4 md:px-5 py-2 md:py-2.5 bg-card dark:bg-dark-surface border border-gray-100 dark:border-dark-border text-text dark:text-dark-text rounded-xl font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 text-sm md:text-base"
              >
                {t("profile.cancel")}
              </button>
              <button
                onClick={confirmSave}
                className="px-4 md:px-5 py-2 md:py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors shadow-lg shadow-primary/20 text-sm md:text-base"
              >
                {t("profile.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl p-5 md:p-6 w-full max-w-md mx-4 border border-gray-100 dark:border-dark-border">
            <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-text dark:text-dark-text">
              {t("profile.confirmAction")}
            </h2>
            <p className="mb-6 md:mb-8 text-sm md:text-base text-gray-600 dark:text-dark-muted">
              {t("profile.confirmPasswordChange")}
            </p>
            <div className="flex justify-end gap-2 md:gap-3">
              <button
                onClick={() => handleConfirmPasswordChange(false)}
                className="px-4 md:px-5 py-2 border rounded-lg transition-colors border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-surface text-sm md:text-base"
              >
                {t("profile.cancel")}
              </button>
              <button
                onClick={() => handleConfirmPasswordChange(true)}
                className="px-4 md:px-5 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm md:text-base"
              >
                {t("profile.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
